import { ITeam, IUserProfile, Team, UserProfile } from "../../../base/common/auth.js";
import { createDecorator } from "../../instantiation/common/registry.js";
import { UserInfoResponse } from "needle-cloud";
import { ILogService } from "../../log/common/log.js";
import { Emitter, Event } from "../../../base/common/events.js";
import { Disposable } from "../../../base/common/lifecycle.js";
import { IAnalyticsService } from "../../analytics/common/analytics.js";
import { IContextKeyService } from "../../contextkey/common/contextkey.js";

export const ContextKeyLoggedIn = 'isLoggedIn';

export const IIdentitiyService = createDecorator<IIdentitiyService>("identityService");

export interface IIdentitiyService {

    // setAuthClient?: (authClient: object) => Promise<void>;

    get me(): IUserProfile | null;
    get isLoggedIn(): boolean;
    get isLoginProcessRunning(): boolean;

    login(): Promise<IUserProfile | null>;
    logout(): Promise<void>;

    teams: readonly ITeam[];

    readonly onDidChange: Event<IIdentitiyService | null>;

}


export abstract class AbstractLogtoIdentitiyService extends Disposable implements IIdentitiyService {
    protected _onDidChange = new Emitter<IIdentitiyService>();
    readonly onDidChange = this._onDidChange.event;

    private readonly isLoggedInKey = this.contextKeyService.createKey<boolean>(ContextKeyLoggedIn, false);

    constructor(
        @ILogService protected readonly logger: ILogService,
        @IAnalyticsService protected readonly analytics: IAnalyticsService | null,
        @IContextKeyService protected readonly contextKeyService: IContextKeyService,
    ) {
        super();
    }


    get me(): IUserProfile {
        return this._me!;
    }

    get teams(): readonly ITeam[] {
        return this._teams;
    }

    get isLoggedIn(): boolean {
        return this._me != null;
    }

    get isLoginProcessRunning(): boolean {
        return this._loginPromise !== null;
    }

    private _loginPromise: Promise<IUserProfile | null> | null = null;
    async login(): Promise<IUserProfile | null> {
        this.logger.info("Starting login process");
        this.analytics?.logEvent("auth:login-attempt", {});
        return this.internalLogin().then(async res => {
            if (res) {
                await this.analytics?.logEvent("auth:login-success", { userId: res.id });
                return this._me;
            }
            else {
                await this.analytics?.logEvent("auth:login-failed", {});
                return null;
            }
        });
    }

    async logout(): Promise<void> {
        this.logger.info("Starting logout process");
        this.analytics?.logEvent("auth:logout", { userId: this._me?.id || "unknown" });
        return this.runLogoutFlow().then(() => {
            this._me = null;
            this._teams.length = 0;
            this._onDidChange.fire(this);
        });
    }


    /** Login without analytics */
    protected async internalLogin(): Promise<IUserProfile | null> {
        if (this._loginPromise) {
            return this._loginPromise;
        }
        this._loginPromise = this.runLoginFlow().then((user) => {
            if (user) {
                this.updateUserProfile(user);
                return this._me;
            }
            return null;
        }).finally(() => {
            this._loginPromise = null;
        });
        return this._loginPromise;
    }

    protected _me: UserProfile | null = null;
    protected readonly _teams: ITeam[] = [];

    protected abstract runLoginFlow(): Promise<UserInfoResponse | null>;
    protected abstract getUserInfo(): Promise<UserInfoResponse | null>;
    protected abstract runLogoutFlow(): Promise<void>;

    protected updateUserProfile(user: UserInfoResponse) {

        this.isLoggedInKey.set(!!user);

        if (user) {
            this.logger.debug("Updating user profile:", user);

            this._me ??= new UserProfile(user.sub, user.name || user.username, user.email || "", user.picture);
            this._me.id = user.sub;
            this._me.name = user.name;
            this._me.email = user.email;
            this._me.avatarUrl = user.picture;

            if (user.organization_data) {
                for (const org of user.organization_data) {
                    const team = this._teams.find(t => t.id === org.id) || new Team(org.id, org.name);
                    team.name = org.name;
                    if (!this._teams.includes(team)) {
                        this._teams.push(team);
                    }
                }
            }
            this.logger.debug("User profile updated:", this.isLoggedIn, this._me, this._teams);
            this._onDidChange.fire(this);
        }
        else if (this._me) {
            this.logger.debug("Clearing user profile");
            this._me = null;
            this._teams.length = 0;
            this._onDidChange.fire(this);
        }
        else {
            this.logger.debug("No user profile to update");
        }
    }
}
